import { __rest } from '../../../_virtual/_tslib.js';
import { useForkRef } from '@mui/material';
import { useRef, useMemo, useCallback, useEffect } from 'react';

const valueSeparator = " - ";
const viewValueSeparator = " – ";
function useRangeInput({ inputProps, ref }) {
    const { format, value, onChange } = inputProps, rest = __rest(inputProps, ["format", "value", "onChange"]);
    const inputRef = useRef(null);
    const timeoutRef = useRef();
    const rangeInputRef = useForkRef(inputRef, ref);
    const startDayRef = useRef("");
    const endDayRef = useRef("");
    const hasFocus = document.activeElement === inputRef.current;
    const sections = useMemo(() => {
        const startValueRangeStart = 0;
        const startValueRangeEnd = format.length;
        const endValueRangeStart = format.length + valueSeparator.length;
        const endValueRangeEnd = endValueRangeStart + format.length;
        return [
            {
                rangeStart: startValueRangeStart,
                rangeEnd: startValueRangeEnd,
            },
            {
                rangeStart: endValueRangeStart,
                rangeEnd: endValueRangeEnd,
            },
        ];
    }, [format]);
    const placeholder = useMemo(() => [format, format].join(viewValueSeparator), [format]);
    const viewValue = useMemo(() => {
        var _a;
        if (!hasFocus && !value) {
            return "";
        }
        const [startValue, endValue] = (_a = value === null || value === void 0 ? void 0 : value.split(valueSeparator)) !== null && _a !== void 0 ? _a : [];
        const startOrPlaceholder = startValue ? startValue : format;
        const endOrPlaceholder = endValue ? endValue : format;
        startDayRef.current = startOrPlaceholder;
        endDayRef.current = endOrPlaceholder;
        return [startOrPlaceholder, endOrPlaceholder].join(viewValueSeparator);
    }, [hasFocus, value, format]);
    const setSelection = useCallback((currentValue = value) => {
        const input = inputRef.current;
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
            var _a;
            // The ref changed, the component got remounted, the focus event is no longer relevant.
            if (!input || input !== inputRef.current) {
                return;
            }
            const [startValue = "", endValue = ""] = (_a = currentValue === null || currentValue === void 0 ? void 0 : currentValue.split(valueSeparator)) !== null && _a !== void 0 ? _a : [];
            const [startSection, endSection] = sections;
            if (!startValue || startValue === format) {
                input.setSelectionRange(startSection.rangeStart, startSection.rangeEnd);
                return;
            }
            if (!endValue || endValue === format) {
                input.setSelectionRange(endSection.rangeStart, endSection.rangeEnd);
                return;
            }
            input.setSelectionRange(startSection.rangeStart, endSection.rangeEnd);
        });
    }, [sections, format, value]);
    useEffect(() => {
        const input = inputRef.current;
        if (!input) {
            return;
        }
        if (startDayRef.current !== format && endDayRef.current === format) {
            input.focus();
            setSelection();
        }
    }, [value, format, setSelection]);
    const handleChange = useCallback((event) => {
        event.preventDefault();
        const targetValue = event.target.value;
        const [newStartDay, newEndDay] = targetValue.split(valueSeparator);
        const isStartDayReset = newStartDay !== startDayRef.current && !newStartDay;
        const isEndDayReset = newEndDay !== endDayRef.current && !newEndDay;
        if (isStartDayReset || isEndDayReset) {
            event.target.value = "";
            onChange === null || onChange === void 0 ? void 0 : onChange(event);
        }
        else {
            event.target.value = viewValue;
        }
        setSelection(event.target.value);
    }, [onChange, viewValue, setSelection]);
    return {
        rangeInputProps: Object.assign(Object.assign({}, rest), { placeholder, value: viewValue, onChange: handleChange, setSelection }),
        rangeInputRef,
        hasStartDay: startDayRef.current !== format,
    };
}

export { useRangeInput, valueSeparator, viewValueSeparator };
//# sourceMappingURL=useRangeInput.js.map
